import { apiFetch, apiRequest } from './core/client';

/**
 * Mail the orders app can show is never read from email_db — it is a SNAPSHOT
 * taken through sundae's bridge at link time and stored here (plan §3c). These
 * endpoints are the orders server's `/api/mail/*` routes, all behind the
 * `orders:read:mail` permission (docs/PERMISSIONS.md).
 */

export interface MailAddress {
  name: string | null;
  address: string | null;
}

export interface MailAttachment {
  id: string;
  filename: string;
  mime: string;
  size: number;
  inline: boolean;
  content_id: string | null;
  /** Blob route on this server: `/api/mail/:message_id/attachments/:id`. */
  url: string;
}

export interface MailSnapshot {
  message_id: string;
  thread_id: string | null;
  subject: string | null;
  from: MailAddress;
  to: MailAddress[];
  cc: MailAddress[];
  sent_at: string | null;
  /**
   * Sanitised server-side by the bridge: remote images stripped, `cid:` images
   * rewritten to `attachment:<id>` placeholders this app resolves against its
   * own blob route.
   */
  html_sanitized: string | null;
  text_plain: string | null;
  attachments: MailAttachment[];
  snapshot_at: string;
  granted_by: string;
}

/** One header row of the live, vendor-filtered thread listing. */
export interface MailThreadMessage {
  message_id: string;
  subject: string | null;
  from: MailAddress;
  sent_at: string | null;
  /** Already snapshotted here — openable without taking a new grant. */
  granted: boolean;
}

export type MailSnapshotKind = 'announcement' | 'invoice' | 'carrier_charge' | 'package' | 'reference';

export async function getMailSnapshot(messageId: string): Promise<MailSnapshot> {
  return apiRequest<MailSnapshot>({
    path: `/mail/${encodeURIComponent(messageId)}`,
    method: 'GET',
  });
}

/**
 * Live thread expansion through the bridge — header-only, filtered to the
 * journey's vendor domains. Never returns bodies; opening one of these rows
 * takes its own message-scope grant via `createMailSnapshot`.
 */
export async function getMailThread(
  messageId: string,
  journeyId?: string | null
): Promise<MailThreadMessage[]> {
  const response = await apiRequest<{ messages: MailThreadMessage[] }>({
    path: `/mail/${encodeURIComponent(messageId)}/thread`,
    method: 'GET',
    query: journeyId ? { journey_id: journeyId } : undefined,
  });
  return Array.isArray(response.messages) ? response.messages : [];
}

/** Grant + snapshot one message chosen from the thread list. */
export async function createMailSnapshot(body: {
  message_id: string;
  journey_id?: string | null;
  kind: MailSnapshotKind;
}): Promise<MailSnapshot> {
  return apiRequest<MailSnapshot>({ path: '/mail/snapshots', method: 'POST', body });
}

export function mailAttachmentUrl(messageId: string, attachmentId: string): string {
  return `/api/mail/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachmentId)}`;
}

/**
 * Attachment bytes. The blob route needs the bearer header, so an <img src> or
 * a preview component pointed straight at the URL would 401 — every consumer
 * goes through here and works from an object URL instead.
 */
export async function fetchMailAttachmentBlob(
  messageId: string,
  attachmentId: string,
  signal?: AbortSignal
): Promise<Blob> {
  const response = await apiFetch({
    path: `/mail/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachmentId)}`,
    method: 'GET',
    signal,
  });
  if (!response.ok) {
    throw new Error(`Failed to load attachment (${response.status})`);
  }
  return response.blob();
}

/** The same message in sundae's reader, for the "Open in sundae" link. */
export function sundaeMessageUrl(messageId: string): string {
  return `https://sundae.kent.pw/#/account/1/email/${encodeURIComponent(messageId)}`;
}
